import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutRectangle, StyleSheet, View } from 'react-native';

import { RouteProp, useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useGameSettingsStore } from '@/feature/game-details-arcade/hooks/use-game-settings';
import { useStartGameAction } from '@/feature/game-details-arcade/hooks/use-start-game-action';
import { useArcadeGames } from '@/feature/lobby-arcade/hooks/use-arcade-games';
import { ArcadeLobbyTabNavigationProp } from '@/navigation/arcade/types';
import { RootStackParamList } from '@/navigation/types';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { GameResult } from '../GameResultItem';
import { PlayerColumn, PlayerLostColumn, PlayerSearchingColumn } from '../PlayerMatchup/PlayerColumn';
import { PlayerMatchup } from '../PlayerMatchup/PlayerMatchup';
import { GRADIENT_HEIGHT, WinBackgroundGradient } from './WinBackgroundGradient';

type GameResultsBaseScreenProps = {
    description?: React.ReactElement;
    playerMatchup: React.ReactElement;
};

const HEADER_HEIGHT = 48;

const styles = StyleSheet.create({
    header: {
        height: HEADER_HEIGHT,
    },
});

const GameResultsBaseScreen = ({ description, playerMatchup }: GameResultsBaseScreenProps) => {
    const { t } = useTranslation(['common', 'arcade_game_results']);
    const navigation = useNavigation<ArcadeLobbyTabNavigationProp>();
    const [playerMatchupLayout, setPlayerMatchupLayout] = useState<LayoutRectangle | undefined>();
    const [descriptionLayout, setDescriptionLayout] = useState<LayoutRectangle | undefined>();
    const [centerY, setCenterY] = useState(0);

    const gameId = useGameSettingsStore(state => state.gameId);
    const { data: arcadeGames } = useArcadeGames();
    const game = arcadeGames?.find(g => g.game_id === gameId);

    const handleClose = () => {
        if (isLoading) return;
        if (game) {
            navigation.popTo('ArcadeHome', {
                screen: 'ArcadeLobby',
                params: {
                    screen: 'ArcadeGameDetailsScreen',
                    params: { game },
                },
            });
        } else {
            // Fallback to arcade lobby if game not found
            navigation.popTo('ArcadeHome', {
                screen: 'ArcadeLobby',
                params: {
                    screen: 'ArcadeLobbyScreen',
                },
            });
        }
    };

    useEffect(() => {
        if (playerMatchupLayout && descriptionLayout) {
            const newCenterY = playerMatchupLayout.y / 2 - descriptionLayout.height / 2 + HEADER_HEIGHT;
            setCenterY(newCenterY);
        }
    }, [playerMatchupLayout, descriptionLayout]);

    const {
        handleStartGame,
        isLoading,
        hasInsufficientRealMoney,
        hasInsufficientBetrBucks,
        isUsingBetrBucks,
        stakeAmount,
        toWinAmount,
    } = useStartGameAction();

    const unableToPlayAgain =
        (isUsingBetrBucks && hasInsufficientBetrBucks) || (!isUsingBetrBucks && hasInsufficientRealMoney);

    return (
        <Screen edges={['top', 'bottom']}>
            <Box flex={1}>
                <ScreenNavBar
                    title={game?.name}
                    closeIconMode="close"
                    titleVariant="titleLarge2"
                    titleOverrideStyle={{ color: ds.colorValue.text.primary }}
                    style={styles.header}
                    onClose={handleClose}
                />
                <Box
                    position="absolute"
                    alignSelf="center"
                    top={centerY}
                    onLayout={e => setDescriptionLayout(e.nativeEvent.layout)}
                >
                    {description}
                </Box>
                <Box flex={1} justifyContent="center" alignItems="center">
                    <Column onLayout={e => setPlayerMatchupLayout(e.nativeEvent.layout)}>{playerMatchup}</Column>
                </Box>
                <Box height={140} p="s16" gap="s12" justifyContent="flex-end">
                    {unableToPlayAgain ? (
                        <Button onPress={handleClose} hierarchy={'subtle'} label={t('done')} />
                    ) : (
                        <>
                            <Text variant="bodyMedium2" color="neutral300" mb="s4" textAlign="center">
                                {t('arcade_game_results:stake_amount', {
                                    amount: toLocaleCurrency(stakeAmount),
                                    to_win_amount: toLocaleCurrency(toWinAmount),
                                })}
                            </Text>
                            <Button
                                hierarchy="primaryLilac"
                                label={t('arcade_game_results:play_again', { amount: toLocaleCurrency(stakeAmount) })}
                                disabled={isLoading}
                                loading={isLoading}
                                onPress={handleStartGame}
                            />
                            <Button onPress={handleClose} hierarchy={'subtle'} label={t('done')} disabled={isLoading} />
                        </>
                    )}
                </Box>
            </Box>
        </Screen>
    );
};

const GameResultsSearchingForOpponentScreen = ({ gameResult }: { gameResult: GameResult }) => {
    const { t } = useTranslation('arcade_game_results');
    return (
        <GameResultsBaseScreen
            description={
                <>
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('still_searching_for_opponent')}
                    </Text>
                    <Text variant="bodyMedium2" color="neutral300">
                        {t('notify_when_matched')}
                    </Text>
                </>
            }
            playerMatchup={
                <PlayerMatchup
                    userColumn={<PlayerColumn playerType="you" score="" avatarUrl={gameResult.avatar_url} />}
                    opponentColumn={<PlayerSearchingColumn />}
                />
            }
        />
    );
};

const GameResultsWonScreen = ({ gameResult }: { gameResult: GameResult }) => {
    const { t } = useTranslation('arcade_game_results');
    const textContainerRef = useRef<View>(null);
    const [textCenterY, setTextCenterY] = useState(0);

    const getCenterCoordinates = () => {
        if (textContainerRef.current) {
            // Measure the position of the "You Won" text, as we want to center the background gradient on it
            textContainerRef.current.measureInWindow((_x: number, y: number, _width: number, height: number) => {
                const centerY = y + height / 2;
                setTextCenterY(centerY);
            });
        }
    };

    return (
        <>
            <WinBackgroundGradient y={textCenterY - GRADIENT_HEIGHT / 2} />
            <GameResultsBaseScreen
                description={
                    <View ref={textContainerRef} onLayout={() => getCenterCoordinates()}>
                        <Text
                            variant="bodyMedium2"
                            fontFamily="Gamay"
                            fontSize={30}
                            fontWeight="800"
                            color={ds.colorToken.text.success}
                            lineHeight={30}
                        >
                            {t('you_won', { amount: toLocaleCurrency(gameResult.amount_won) })}
                        </Text>
                    </View>
                }
                playerMatchup={
                    <>
                        <Box justifyContent="center" alignItems="center">
                            <PlayerMatchup
                                userColumn={
                                    <PlayerColumn
                                        playerType="you"
                                        score={gameResult.score}
                                        avatarUrl={gameResult.avatar_url}
                                    />
                                }
                                opponentColumn={
                                    <PlayerLostColumn
                                        playerType="opponent"
                                        score={gameResult.opponents[0]?.score}
                                        username={gameResult.opponents[0]?.username}
                                        avatarUrl={gameResult.opponents[0]?.avatar_url}
                                    />
                                }
                            />
                        </Box>
                    </>
                }
            />
        </>
    );
};

const GameResultsLostScreen = ({ gameResult }: { gameResult: GameResult }) => {
    const { t } = useTranslation();
    return (
        <GameResultsBaseScreen
            description={
                <Text
                    variant="bodyMedium2"
                    fontFamily="Gamay"
                    fontSize={30}
                    fontWeight="800"
                    color="neutral300"
                    textTransform="uppercase"
                    lineHeight={30}
                >
                    {t('lost')}
                </Text>
            }
            playerMatchup={
                <Box justifyContent="center" alignItems="center">
                    <PlayerMatchup
                        userColumn={
                            <PlayerLostColumn
                                playerType="you"
                                score={gameResult.score}
                                avatarUrl={gameResult.avatar_url}
                            />
                        }
                        opponentColumn={
                            <PlayerColumn
                                playerType="opponent"
                                score={gameResult.opponents[0]?.score}
                                username={gameResult.opponents[0]?.username}
                                avatarUrl={gameResult.opponents[0]?.avatar_url}
                            />
                        }
                    />
                </Box>
            }
        />
    );
};

const GameResultsRefundedScreen = ({ gameResult }: { gameResult: GameResult }) => {
    const { t } = useTranslation();
    return (
        <GameResultsBaseScreen
            description={
                <Text variant="bodyMedium2" color="neutral300">
                    {t('refunded')} {toLocaleCurrency(gameResult.stake)}
                </Text>
            }
            playerMatchup={
                <PlayerMatchup
                    userColumn={
                        <PlayerColumn playerType="you" score={gameResult.score} avatarUrl={gameResult.avatar_url} />
                    }
                    opponentColumn={
                        <PlayerColumn
                            playerType="opponent"
                            score={gameResult.opponents[0]?.score}
                            username={gameResult.opponents[0]?.username}
                            avatarUrl={gameResult.opponents[0]?.avatar_url}
                        />
                    }
                />
            }
        />
    );
};

export const GameResultsScreen = ({ route }: { route: RouteProp<RootStackParamList, 'ArcadeGameResults'> }) => {
    const { gameResult } = route.params;
    switch (gameResult.status) {
        case 'PENDING':
            return <GameResultsSearchingForOpponentScreen gameResult={gameResult} />;
        case 'WON':
            return <GameResultsWonScreen gameResult={gameResult} />;
        case 'LOST':
            return <GameResultsLostScreen gameResult={gameResult} />;
        case 'REFUNDED':
        case 'DRAW':
            return <GameResultsRefundedScreen gameResult={gameResult} />;
    }
};
